import { CardStatsSchema, ListingListResponseSchema, CardDetailResponseSchema, CardListResponseSchema, LinkRequestsResponseSchema, NotificationListResponseSchema, CatalogSearchResponseSchema, PortfolioItemResponseSchema, PortfolioListResponseSchema, CreateUserResponseSchema, ListingResearchResponseSchema, MeResponseSchema, ProblemDetailsSchema, ROUTES, ResearchListResponseSchema, ResearchResponseSchema, ResearchBulkResponseSchema, ResearchSummarySchema, SaveResearchResponseSchema, SearchResponseSchema, UserListResponseSchema, UserResponseSchema, } from "@pokifolio/contracts";
import { z } from "zod";
import { ApiError } from "./api-error.js";
/** Error body returned by the authentication endpoints (/api/auth/*). */
const AuthErrorSchema = z.object({ code: z.string().optional(), message: z.string().optional() });
const AUTH_ERROR_MESSAGES = {
    INVALID_EMAIL_OR_PASSWORD: { code: "unauthenticated", detail: "Email or password is incorrect." },
    BANNED_USER: {
        code: "permission_denied",
        detail: "This account is deactivated. Contact your administrator.",
    },
};
const NETWORK_ERROR = new ApiError({
    status: 0,
    code: "dependency_unavailable",
    title: "Cannot reach the server",
    detail: "Cannot reach the Pokifolio server. Check your connection and try again.",
});
const PlatformListSchema = z.object({ data: z.array(z.string()) });
export function createApiClient(options) {
    const fetchFn = options.fetch ?? ((input, init) => fetch(input, init));
    const base = options.baseUrl.replace(/\/$/, "");
    async function send(method, path, body, signal) {
        // A file (Blob) is sent as it is, with its own type; anything else as JSON.
        const isFile = body instanceof Blob;
        try {
            return await fetchFn(`${base}${path}`, {
                method,
                credentials: "include",
                headers: body === undefined
                    ? { Accept: "application/json" }
                    : {
                        Accept: "application/json",
                        "Content-Type": isFile ? body.type || "application/octet-stream" : "application/json",
                    },
                ...(body === undefined ? {} : { body: isFile ? body : JSON.stringify(body) }),
                ...(signal === undefined ? {} : { signal }),
            });
        }
        catch (error) {
            // A cancelled request is not a network problem; let the caller see the cancellation.
            if (signal?.aborted === true)
                throw error;
            throw NETWORK_ERROR;
        }
    }
    async function readJson(res) {
        const text = await res.text();
        if (text.length === 0)
            return undefined;
        try {
            return JSON.parse(text);
        }
        catch {
            return undefined;
        }
    }
    async function toError(res) {
        const body = await readJson(res);
        const requestId = res.headers.get("x-request-id") ?? undefined;
        const problem = ProblemDetailsSchema.safeParse(body);
        if (problem.success) {
            return new ApiError({
                status: problem.data.status,
                code: problem.data.code,
                title: problem.data.title,
                detail: problem.data.detail,
                requestId: problem.data.requestId ?? requestId,
                fieldErrors: problem.data.errors ?? [],
            });
        }
        const authError = AuthErrorSchema.safeParse(body);
        const known = authError.success && authError.data.code !== undefined
            ? AUTH_ERROR_MESSAGES[authError.data.code]
            : undefined;
        if (known !== undefined)
            return new ApiError({ status: res.status, title: known.detail, requestId, ...known });
        if (res.status === 429) {
            return new ApiError({
                status: 429,
                code: "rate_limited",
                title: "Too many attempts",
                detail: "Too many attempts. Please wait a few minutes and try again.",
                requestId,
            });
        }
        return new ApiError({
            status: res.status,
            code: res.status >= 500 ? "internal_error" : "bad_request",
            title: "Request failed",
            detail: authError.success ? authError.data.message : undefined,
            requestId,
        });
    }
    /** The server answered with a shape the contracts do not allow (e.g. an outdated deployment). */
    function unexpectedResponse(res) {
        return new ApiError({
            status: res.status,
            code: "internal_error",
            title: "Unexpected response",
            detail: "The server sent an unexpected response. Please refresh and try again.",
            requestId: res.headers.get("x-request-id") ?? undefined,
        });
    }
    async function parseOk(res, schema) {
        const parsed = schema.safeParse(await readJson(res));
        if (!parsed.success)
            throw unexpectedResponse(res);
        return parsed.data;
    }
    async function call(method, path, schema, body, signal) {
        const res = await send(method, path, body, signal);
        if (!res.ok)
            throw await toError(res);
        return parseOk(res, schema);
    }
    async function callNoContent(method, path, body) {
        const res = await send(method, path, body);
        if (!res.ok)
            throw await toError(res);
    }
    return {
        signIn: (body) => callNoContent("POST", ROUTES.signIn, body),
        signOut: () => callNoContent("POST", ROUTES.signOut, {}),
        me: async () => {
            const res = await send("GET", ROUTES.me);
            if (res.status === 401)
                return null;
            if (!res.ok)
                throw await toError(res);
            return parseOk(res, MeResponseSchema);
        },
        changePassword: (body) => callNoContent("POST", ROUTES.mePassword, body),
        listUsers: async () => (await call("GET", ROUTES.users, UserListResponseSchema)).data,
        createUser: (body) => call("POST", ROUTES.users, CreateUserResponseSchema, body),
        updateUser: async (id, body) => (await call("PATCH", ROUTES.user(id), UserResponseSchema, body)).user,
        deactivateUser: async (id) => (await call("POST", ROUTES.userDeactivate(id), UserResponseSchema)).user,
        reactivateUser: async (id) => (await call("POST", ROUTES.userReactivate(id), UserResponseSchema)).user,
        setUserPermissions: async (id, permissions) => (await call("PUT", ROUTES.userPermissions(id), UserResponseSchema, { permissions })).user,
        searchCatalog: async (q, signal) => (await call("GET", `${ROUTES.catalogCards}?${new URLSearchParams({ q }).toString()}`, CatalogSearchResponseSchema, undefined, signal)).data,
        runSearch: (body) => call("POST", ROUTES.searches, SearchResponseSchema, body),
        saveResearch: (body) => call("POST", ROUTES.research, SaveResearchResponseSchema, body),
        listResearch: (query) => {
            const params = new URLSearchParams();
            if (query.scope !== undefined)
                params.set("scope", query.scope);
            if (query.status !== undefined)
                params.set("status", query.status);
            if (query.q !== undefined && query.q !== "")
                params.set("q", query.q);
            if (query.sort !== undefined)
                params.set("sort", query.sort);
            if (query.dir !== undefined)
                params.set("dir", query.dir);
            if (query.page !== undefined)
                params.set("page", String(query.page));
            if (query.pageSize !== undefined)
                params.set("pageSize", String(query.pageSize));
            const search = params.toString();
            return call("GET", search === "" ? ROUTES.research : `${ROUTES.research}?${search}`, ResearchListResponseSchema);
        },
        getResearch: (id) => call("GET", ROUTES.researchRecord(id), ResearchResponseSchema),
        researchSummary: () => call("GET", ROUTES.researchSummary, ResearchSummarySchema),
        saveAssessment: (id, body) => call("PUT", ROUTES.researchAssessment(id), ResearchResponseSchema, body),
        confirmPrice: (id, body) => call("PUT", ROUTES.researchPrice(id), ResearchResponseSchema, body),
        researchStep: (id, step, body) => call("POST", ROUTES.researchStep(id, step), ResearchResponseSchema, body),
        deleteResearch: (id, version) => callNoContent("DELETE", `${ROUTES.researchRecord(id)}?version=${String(version)}`),
        researchBulk: (body) => call("POST", ROUTES.researchBulk, ResearchBulkResponseSchema, body),
        listCards: (query) => {
            const params = new URLSearchParams();
            if (query.q !== undefined && query.q !== "")
                params.set("q", query.q);
            if (query.scope !== undefined)
                params.set("scope", query.scope);
            if (query.page !== undefined)
                params.set("page", String(query.page));
            if (query.pageSize !== undefined)
                params.set("pageSize", String(query.pageSize));
            const search = params.toString();
            return call("GET", search === "" ? ROUTES.cards : `${ROUTES.cards}?${search}`, CardListResponseSchema);
        },
        getCard: (id) => call("GET", ROUTES.card(id), CardDetailResponseSchema),
        addCard: (body) => call("POST", ROUTES.cards, CardDetailResponseSchema, body),
        renameCard: (id, body) => call("PATCH", ROUTES.card(id), CardDetailResponseSchema, body),
        deleteCard: (id) => callNoContent("DELETE", ROUTES.card(id)),
        addCardLink: (id, body) => call("POST", ROUTES.cardLinks(id), CardDetailResponseSchema, body),
        updateCardLink: (id, linkId, body) => call("PUT", ROUTES.cardLink(id, linkId), CardDetailResponseSchema, body),
        deleteCardLink: (id, linkId) => call("DELETE", ROUTES.cardLink(id, linkId), CardDetailResponseSchema),
        askAboutLink: (id, linkId) => call("POST", ROUTES.cardLinkStep(id, linkId, "ask"), CardDetailResponseSchema, {}),
        approveLink: (id, linkId, body) => call("POST", ROUTES.cardLinkStep(id, linkId, "approve"), CardDetailResponseSchema, body),
        declineLink: (id, linkId, body) => call("POST", ROUTES.cardLinkStep(id, linkId, "decline"), CardDetailResponseSchema, body),
        linkRequests: () => call("GET", ROUTES.cardRequests, LinkRequestsResponseSchema),
        listListings: (query) => {
            const params = new URLSearchParams();
            for (const [key, value] of Object.entries(query))
                if (String(value) !== "")
                    params.set(key, String(value));
            const search = params.toString();
            return call("GET", search === "" ? ROUTES.cardListings : `${ROUTES.cardListings}?${search}`, ListingListResponseSchema);
        },
        setCardImage: (id, image) => call("PUT", ROUTES.cardImage(id), CardDetailResponseSchema, image),
        cardImageUrl: (id, version) => `${base}${ROUTES.cardImage(id)}?v=${encodeURIComponent(version)}`,
        markCardViewed: (id) => callNoContent("POST", ROUTES.cardViewed(id), {}),
        setCardClosed: (id, body) => call("POST", ROUTES.cardClosed(id), CardDetailResponseSchema, body),
        updateLinkDeal: (id, linkId, body) => call("PATCH", ROUTES.cardLinkDeal(id, linkId), CardDetailResponseSchema, body),
        cardPlatforms: async () => (await call("GET", ROUTES.cardPlatforms, PlatformListSchema)).data,
        cardStats: (scope = "all") => call("GET", `${ROUTES.cardStats}?scope=${scope}`, CardStatsSchema),
        notifications: () => call("GET", ROUTES.notifications, NotificationListResponseSchema),
        markNotificationsRead: () => callNoContent("POST", ROUTES.notificationsRead),
        listPortfolio: (query) => {
            const params = new URLSearchParams();
            if (query.q !== undefined && query.q !== "")
                params.set("q", query.q);
            if (query.cardType !== undefined)
                params.set("cardType", query.cardType);
            if (query.sort !== undefined)
                params.set("sort", query.sort);
            if (query.dir !== undefined)
                params.set("dir", query.dir);
            if (query.page !== undefined)
                params.set("page", String(query.page));
            if (query.pageSize !== undefined)
                params.set("pageSize", String(query.pageSize));
            const search = params.toString();
            return call("GET", search === "" ? ROUTES.portfolio : `${ROUTES.portfolio}?${search}`, PortfolioListResponseSchema);
        },
        getPortfolioItem: async (id) => (await call("GET", ROUTES.portfolioItem(id), PortfolioItemResponseSchema)).item,
        addPortfolioItem: async (body) => (await call("POST", ROUTES.portfolio, PortfolioItemResponseSchema, body)).item,
        updatePortfolioItem: async (id, body) => (await call("PUT", ROUTES.portfolioItem(id), PortfolioItemResponseSchema, body)).item,
        removePortfolioItem: (id) => callNoContent("DELETE", ROUTES.portfolioItem(id)),
        addPortfolioPhoto: async (id, photo) => (await call("POST", ROUTES.portfolioPhotos(id), PortfolioItemResponseSchema, photo)).item,
        removePortfolioPhoto: async (id, photoId) => (await call("DELETE", ROUTES.portfolioPhoto(id, photoId), PortfolioItemResponseSchema)).item,
        portfolioPhotoUrl: (id, photoId) => `${base}${ROUTES.portfolioPhoto(id, photoId)}`,
        researchForListings: async (listingIds) => listingIds.length === 0
            ? []
            : (await call("GET", `${ROUTES.researchByListing}?${new URLSearchParams({ ids: listingIds.join(",") }).toString()}`, ListingResearchResponseSchema)).data,
    };
}
//# sourceMappingURL=client.js.map